import { ArrowLeftIcon, ArrowsRightLeftIcon, SparklesIcon } from '@heroicons/react/24/outline';
import SharedButton from '../../../../global/Button/Button';
import SharedBadge from '@/global/Badge/Badge';
import {
  DetailGroup,
  getRowDetailContextLabel,
  isSentenceHtmlDetailKey,
  normalizeDetailValue,
  RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX,
  SHOW_HISTORIAL_SCORE,
} from '../utils/searchResultsHelpers';
import TabSelector from '@/global/TabSelector/TabSelector';
import { HistoricalScoreTabContent } from './HistoricalScoreTabContent';
import { getDetailValueLink, openEntityInfoPanel } from '../utils/detailValueLinks';
import { BottomDetailValueWithTooltip } from './BottomDetailValueWithTooltip';
import { renderDetailGroupsWithGtp } from '@/components/GtpBreakdown/GtpDetailCard';

interface RelationSummary {
  leftEntity: string;
  rightEntity: string;
  showRelationArrow: boolean;
  diseaseType: string;
  score: string;
  pmids: string;
  chem: string;
  variant: string;
}

interface FullRecordDetailViewProps {
  bottomDetailRow: any;
  groupedBottomDetails: DetailGroup[];
  relationSummary: RelationSummary;
  source: any;
  tab: any;
  isSidebarOpen: boolean;
  sidebarWidth: number | string;
  isDesktop: boolean;
  hasLeftPanel?: boolean;
  searchParams: any;
  setSearchParams: (params: any) => void;
  handleDownloadRowDetail: () => void;
  bottomDetailTab: 'summary' | 'historical';
  setBottomDetailTab: (tab: 'summary' | 'historical') => void;
  ensureTablesViewForDrilldown?: () => void;
  isAcademic?: boolean;
  isAuthenticated?: boolean;
}

export function FullRecordDetailView({
  bottomDetailRow,
  groupedBottomDetails,
  relationSummary,
  source,
  tab,
  isSidebarOpen,
  sidebarWidth,
  isDesktop,
  hasLeftPanel = false,
  searchParams,
  setSearchParams,
  handleDownloadRowDetail,
  bottomDetailTab,
  setBottomDetailTab,
  ensureTablesViewForDrilldown,
  isAcademic = false,
  isAuthenticated = false,
}: FullRecordDetailViewProps) {
  if (!bottomDetailRow) return null;
  const renderEntryValue = (entry: { key: string; value: unknown }) => {
    const linkedValue = getDetailValueLink(entry.key, entry.value);
    if (linkedValue?.type === 'external') {
      const textValue = normalizeDetailValue(entry.value, entry.key);
      return (
        <a
          className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-pink underline underline-offset-2"
          href={linkedValue.href}
          target="_blank"
          rel="noopener noreferrer"
          title={textValue}
        >
          {textValue}
        </a>
      );
    }
    if (linkedValue?.type === 'entity') {
      const textValue = normalizeDetailValue(entry.value, entry.key);
      return (
        <button
          type="button"
          className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap bg-transparent p-0 text-left font-semibold text-pink underline underline-offset-2"
          title={textValue}
          onClick={() => openEntityInfoPanel(linkedValue.entityView, linkedValue.item)}
        >
          {textValue}
        </button>
      );
    }
    const isSentenceHtml = isSentenceHtmlDetailKey(entry.key);
    const sentenceHtml = isSentenceHtml && typeof entry.value === 'string' ? entry.value.trim() : '';
    return sentenceHtml ? (
      <BottomDetailValueWithTooltip html={sentenceHtml} allowCopy />
    ) : (
      <span
        className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-slate-800"
        title={normalizeDetailValue(entry.value, entry.key)}
      >
        {normalizeDetailValue(entry.value, entry.key)}
      </span>
    );
  };

  const marginRight = isDesktop
    ? isSidebarOpen
      ? typeof sidebarWidth === 'number'
        ? sidebarWidth + 20
        : `calc(${sidebarWidth} + 20px)`
      : RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX + 20
    : 0;
  const marginLeft = isDesktop && hasLeftPanel ? 356 : 16;

  const showHistoricalScoreTab = SHOW_HISTORIAL_SCORE && String(tab ?? '').toUpperCase() === 'GDA';
  const isHistoricalScoreRestricted = isAcademic && isAuthenticated;
  const activeBottomDetailTab =
    isHistoricalScoreRestricted && bottomDetailTab === 'historical' ? 'summary' : bottomDetailTab;

  return (
    <div
      className="mt-4 ml-4 rounded-xl border border-slate-200 bg-white shadow-sm"
      style={{ marginRight, marginLeft, transition: 'margin-right 0.3s ease-out, margin-left 0.3s ease-out' }}
    >
      <button
        type="button"
        className="mb-3 text-sm font-medium text-slate-600 p-4 w-full text-start flex items-center shadow-sm"
        onClick={() => {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete('fullRecord');
          setSearchParams(nextParams);
        }}
      >
        <ArrowLeftIcon className="w-4 h-4 mr-2" strokeWidth={2} /> Back to results
      </button>
      <div className="mb-3 flex items-center justify-between p-4 border border-slate-200 m-4 rounded-3xl bg-gradient-to-r from-pink-50 to-white">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-pink" />
            <span className="text-xs font-normal text-gray-400 tracking-widest text-uppercase">
              {getRowDetailContextLabel(tab as string)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-3xl font-semibold text-uppercase">{relationSummary.leftEntity}</span>
              {relationSummary.showRelationArrow && (
                <>
                  <ArrowsRightLeftIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-3xl font-normal">{relationSummary.rightEntity}</span>
                </>
              )}
            </div>
          </div>
          <div>
            <span className="text-xs font-normal text-gray-400">{relationSummary.diseaseType}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SharedBadge
            text={`Source: ${source}`}
            icon={<SparklesIcon className="w-4 h-4 mr-1" />}
            variant="primaryLight"
            className="mt-2"
          />
          <SharedButton type="button" variant="primaryOutline" size="xs" onClick={handleDownloadRowDetail}>
            Download detail
          </SharedButton>
        </div>
      </div>
      {showHistoricalScoreTab && (
        <div
          className="mx-4 border-b border-slate-200 px-4 py-2"
          role="tablist"
          aria-label="Record detail sections"
        >
          <TabSelector
            options={[
              { label: 'Summary', value: 'summary' },
              {
                label: 'Historical score',
                value: 'historical',
                disabled: isHistoricalScoreRestricted,
                disabledTooltip: 'Upgrade your account to use this tool',
              },
            ]}
            variant="primary"
            size="2xs"
            value={activeBottomDetailTab}
            onChange={v => setBottomDetailTab(v as 'summary' | 'historical')}
          />
        </div>
      )}
      {showHistoricalScoreTab && activeBottomDetailTab === 'historical' ? (
        <div className="p-4 mx-4 mb-4 mt-2 rounded-xl border border-slate-200 bg-white min-h-[200px]">
          <HistoricalScoreTabContent
            row={bottomDetailRow}
            source={String(source)}
            tab={String(tab)}
            ensureTablesViewForDrilldown={ensureTablesViewForDrilldown}
          />
        </div>
      ) : (
        <div className="flex flex-nowrap gap-3 overflow-x-auto p-4">
          {renderDetailGroupsWithGtp({
            groups: groupedBottomDetails,
            row: bottomDetailRow,
            cardClassName: 'shrink-0 flex flex-col',
            cardStyle: { flex: '1.3 1 360px', minWidth: '360px' },
            renderGroup: group => (
            <section
              key={`bottom-${group.title}`}
              className="shrink-0 overflow-hidden rounded-xl border border-slate-200 pt-0 pb-3 flex flex-col"
              style={{ flex: '1 1 0', minWidth: '0' }}
            >
              <h3 className="mb-2 flex min-h-[2.25rem] items-center justify-between gap-2 text-xs font-semibold uppercase tracking-widest text-slate-600 py-2 px-3 bg-gradient-to-r from-pink-50 to-white rounded-t-xl border-b border-slate-200">
                <span className="min-w-0 truncate">• {group.title}</span>
                <span
                  className="shrink-0 tabular-nums font-light text-gray-500"
                  aria-label={`${group.entries.length} fields in this group`}
                  style={{ fontSize: '0.7rem' }}
                >
                  {group.entries.length}
                </span>
              </h3>
              <div className="space-y-1.5 px-3 pt-2">
                {group.entries.map(entry => {
                  return (
                    <div
                      key={`${group.title}-${entry.key}`}
                      className="mb-3 flex flex-col gap-1 text-start text-xs"
                    >
                      <span
                        className="overflow-hidden text-ellipsis whitespace-nowrap text-slate-500 tracking-wide"
                        style={{ fontSize: '0.65rem' }}
                        title={entry.displayLabel}
                      >
                        {entry.displayLabel}
                      </span>
                      {renderEntryValue(entry)}
                    </div>
                  );
                })}
              </div>
            </section>
            ),
          })}
        </div>
      )}
    </div>
  );
}
